import React, { useMemo } from 'react';

import { paths } from '../../../routes/paths';
import { visibleTenantRoutes } from '../../../routes/domains';
import { TenantRouteMeta } from '../../../routes/types';
import { PermissionCode, SECTION_GATES } from '../../../auth/permissionCatalog';
import { SIDEBAR_GROUPS, SIDEBAR_EXTERNAL_ITEMS, SidebarGroupConfig } from './groupsConfig';

/**
 * The shape the sidebar actually renders. One of:
 *   - `leaf`: single item that navigates to a path (or opens an external URL)
 *   - `expandable`: a group containing multiple child items
 */
export type SidebarNavItem =
  | {
      type: 'leaf';
      key: string;
      label: string;
      icon: React.ComponentType<{ sx?: any }>;
      order: number;
      path?: string;
      externalUrl?: string;
      adminOnly?: boolean;
    }
  | {
      type: 'expandable';
      key: string;
      label: string;
      icon: React.ComponentType<{ sx?: any }>;
      order: number;
      adminOnly?: boolean;
      children: Array<{
        label: string;
        path: string;
        order: number;
        adminOnly?: boolean;
        /** False when this entry is an `alsoIn` echo of a route owned elsewhere. */
        isPrimary?: boolean;
      }>;
    };

interface UseSidebarNavOptions {
  /** Tenant code used to build full URLs for each item. */
  tenantCode: string;
  /**
   * When true (super-admin), admin-only groups/items are included regardless
   * of role, and every `requiredRole` group is satisfied. Super-admin sees all.
   */
  includeAdmin: boolean;
  /**
   * The current user's granted role codes. A group with `requiredRole` is shown
   * when the user is super-admin (`includeAdmin`) OR holds that role. This is
   * how additive capability roles (e.g. `flex`) reveal their own groups without
   * granting full admin. Consulted only when `rbacV2Enabled` is false.
   */
  roles: string[];
  /** True when the active tenant is the master tenant — gates `masterOnly` items. */
  isMaster: boolean;
  /** True when the active tenant has Task V2 enabled — hides `hideWhenTaskV2` items. */
  taskV2Enabled: boolean;
  /**
   * True when the active tenant is in Storyblok-only content mode (CPT-1219).
   * Shows `storyblok: 'only'` routes (the Content group) and hides
   * `storyblok: 'legacy'` ones (the CMS group); false does the reverse.
   */
  storyblokOnly: boolean;
  /**
   * The environment selected in the top-bar dropdown. External items that are
   * deployed per environment resolve their URL from it, so following a link
   * lands in the environment the portal is currently pointed at.
   */
  currentEnv: string;
  /**
   * True when the tenant has the `hideV1Actions` attribute on. Hides the V1
   * Actions group outright and renames Task V2 to plain "Actions" — once V1 is
   * gone the "V2" in every label distinguishes it from nothing.
   */
  hideV1Actions: boolean;
  /** RBAC_V2 flag — switches group visibility from `requiredRole` to `SECTION_GATES`. */
  rbacV2Enabled: boolean;
  /** RBAC_V2 permission check (see `useCurrentUser`). Unused when the flag is off. */
  hasPermission: (code: PermissionCode) => boolean;
}

/**
 * Derive the sidebar nav from route metadata + group config.
 *
 * Rules:
 *   1. Every route with `sidebar.group` is collected, grouped by that key.
 *   2. Groups are looked up in SIDEBAR_GROUPS for label + icon + order.
 *   3. A group with one route renders as a leaf (label from the group,
 *      path from the route — matches the historical "single-item group"
 *      pattern like "Dashboard" or "Tenant").
 *   4. A group with two or more routes renders as an expandable section.
 *   5. External items (e.g. "Build in Flex") are merged in as leaves.
 *   6. `includeAdmin` filters out group-level and item-level admin gates.
 *   7. Unknown group keys (route references a group that doesn't exist
 *      in SIDEBAR_GROUPS) are logged in dev and dropped — misconfig guard.
 */
/**
 * Content-surface visibility for one route (CPT-1219). Ungated routes always
 * pass; a gated route passes only in its own mode, so a tenant sees exactly
 * one of Content / CMS. Shares the `storyblok` route field with
 * `RequireStoryblokMode` so nav and route guard cannot disagree.
 */
/**
 * One appearance of a route in the sidebar. A route normally has exactly one,
 * but `sidebar.alsoIn` gives it a second (CPT-1375): the same path listed under
 * two groups, with its own label/order and its own storyblok exemption. Keeping
 * the label/order on the PLACEMENT rather than reading them off the route is
 * what lets the two entries differ while still pointing at one page.
 */
interface NavPlacement {
  route: TenantRouteMeta;
  label: string;
  order: number;
  adminOnly?: boolean;
  /** This placement is exempt from the route's `storyblok` gate. */
  ignoreStoryblokGate?: boolean;
  /**
   * True for the route's own `sidebar.group`, false for an `alsoIn` echo.
   *
   * Two placements share one path, so "is this the active page?" is no longer
   * answerable from the pathname alone — both would say yes and both would
   * light up. The primary placement is the tie-break when the user has not
   * navigated through either group yet (a deep link, a refresh).
   */
  isPrimary: boolean;
}

function storyblokVisible(route: { storyblok?: 'only' | 'legacy' }, storyblokOnly: boolean): boolean {
  if (!route.storyblok) return true;
  return route.storyblok === 'only' ? storyblokOnly : !storyblokOnly;
}

export function useSidebarNav({
  tenantCode,
  includeAdmin,
  roles,
  isMaster,
  taskV2Enabled,
  storyblokOnly,
  currentEnv,
  hideV1Actions,
  rbacV2Enabled,
  hasPermission,
}: UseSidebarNavOptions): SidebarNavItem[] {
  return useMemo(() => {
    if (!tenantCode) return [];

    // Gate for both `SIDEBAR_GROUPS` entries and `SIDEBAR_EXTERNAL_ITEMS` — both
    // shapes carry `key` + optional `requiredRole`; only groups carry the v2-only
    // flags, which external items never set (so they're simply `undefined`).
    //
    // Flag off (legacy): a `requiredRole` group is visible to super-admins
    // (includeAdmin) or to any user holding that specific role. Groups without
    // a requiredRole are public. `onlyWhenV2` groups (e.g. `insights`) don't
    // exist yet, so they're hidden outright.
    //
    // Flag on: `hiddenWhenV2` groups (e.g. `analytics`, replaced by `insights`)
    // are hidden outright; everything else is looked up in `SECTION_GATES` —
    // `'super-admin'` mirrors `includeAdmin`, an array is an anyOf permission
    // check. Every key referenced here MUST have a `SECTION_GATES` entry
    // (dev-warned below) — this mirrors the unknown-group warning for routes.
    const canSeeGroup = (g: { key: string; requiredRole?: string; hiddenWhenV2?: boolean; onlyWhenV2?: boolean }): boolean => {
      if (rbacV2Enabled) {
        if (g.hiddenWhenV2) return false;
        const gate = SECTION_GATES[g.key];
        if (!gate) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`[useSidebarNav] group "${g.key}" has no SECTION_GATES entry — hiding it`);
          }
          return false;
        }
        return gate === 'super-admin' ? includeAdmin : gate.some(hasPermission);
      }
      if (g.onlyWhenV2) return false;
      return !g.requiredRole || includeAdmin || roles.includes(g.requiredRole);
    };

    // An `adminOnly` ITEM opens under RBAC_V2 when the route carries its own
    // GRANTABLE permission the user holds (e.g. pdf-to-html, which shipped
    // super-admin-locked but is assignable via `pdf_to_html`). Routes marked
    // `permission: 'super-admin'` — the Tenant/Config/Dashboard carve-outs —
    // never open this way, preserving the carve-out design. Flag off → false,
    // so legacy adminOnly behavior is untouched.
    const grantedByV2 = (r: { permission?: string }): boolean =>
      rbacV2Enabled &&
      !!r.permission &&
      r.permission !== 'super-admin' &&
      hasPermission(r.permission as PermissionCode);

    const routes = visibleTenantRoutes();
    const groupsByKey = new Map<string, SidebarGroupConfig>(SIDEBAR_GROUPS.map((g) => [g.key, g]));

    // Bucket PLACEMENTS by group. A route contributes one placement for its
    // `sidebar.group`, and a second for `sidebar.alsoIn` when present — the same
    // path listed under two groups (CPT-1375).
    const routesByGroup = new Map<string, NavPlacement[]>();
    const addPlacement = (r: TenantRouteMeta, p: NavPlacement, key: string) => {
      if (!groupsByKey.has(key)) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`[useSidebarNav] route "${r.path}" references unknown sidebar group "${key}"`);
        }
        return;
      }
      const arr = routesByGroup.get(key) ?? [];
      arr.push(p);
      routesByGroup.set(key, arr);
    };
    for (const r of routes) {
      if (!r.sidebar) continue;
      addPlacement(r, {
        route: r,
        label: (hideV1Actions && r.sidebar.labelWhenV1ActionsHidden) || r.sidebar.label,
        order: r.sidebar.order ?? 0,
        adminOnly: r.sidebar.adminOnly,
        isPrimary: true,
      }, r.sidebar.group);

      const also = r.sidebar.alsoIn;
      if (also) {
        addPlacement(r, {
          route: r,
          label: also.label ?? r.sidebar.label,
          order: also.order ?? r.sidebar.order ?? 0,
          adminOnly: r.sidebar.adminOnly,
          ignoreStoryblokGate: also.ignoreStoryblokGate,
          isPrimary: false,
        }, also.group);
      }
    }

    const items: SidebarNavItem[] = [];

    // Route-backed groups
    for (const group of SIDEBAR_GROUPS) {
      const groupAdmin = !!group.requiredRole;
      if (!canSeeGroup(group)) continue;
      // The whole V1 Actions surface goes, rather than individual items: the
      // point of the attribute is that this tenant does not have V1 actions.
      if (hideV1Actions && group.isV1Actions) continue;

      const inGroup = routesByGroup.get(group.key) ?? [];
      if (inGroup.length === 0) continue;

      const groupLabel = (hideV1Actions && group.labelWhenV1ActionsHidden) || group.label;

      // Sort children by placement order, then label
      const sorted = [...inGroup].sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.label.localeCompare(b.label);
      });

      // A placement is visible when its route passes every per-item gate. The
      // storyblok gate is the one a placement can opt out of (`alsoIn`), so the
      // same page can sit under Flex regardless of the tenant's content surface.
      const visible = (p: NavPlacement): boolean => {
        const r = p.route;
        if (p.adminOnly && !includeAdmin && !grantedByV2(r)) return false;
        if (r.sidebar?.masterOnly && !isMaster) return false;
        if (r.sidebar?.hideWhenTaskV2 && taskV2Enabled) return false;
        if (r.sidebar?.showWhenTaskV2 && !taskV2Enabled) return false;
        if (!p.ignoreStoryblokGate && !storyblokVisible(r, storyblokOnly)) return false;
        return true;
      };

      if (sorted.length === 1) {
        // Single-route group → leaf using the group label + group icon
        const only = sorted[0];
        if (!visible(only)) continue;
        items.push({
          type: 'leaf',
          key: group.key,
          label: groupLabel,
          icon: group.icon,
          order: group.order,
          path: `${paths.tenant(tenantCode)}/${only.route.path}`,
          adminOnly: groupAdmin || only.adminOnly,
        });
      } else {
        // Multi-route group → expandable
        const children = sorted
          .filter(visible)
          .map((p) => ({
            label: p.label,
            path: `${paths.tenant(tenantCode)}/${p.route.path}`,
            order: p.order,
            adminOnly: p.adminOnly,
            isPrimary: p.isPrimary,
          }));
        if (children.length === 0) continue;
        items.push({
          type: 'expandable',
          key: group.key,
          label: groupLabel,
          icon: group.icon,
          order: group.order,
          adminOnly: groupAdmin,
          children,
        });
      }
    }

    // External items
    for (const ext of SIDEBAR_EXTERNAL_ITEMS) {
      if (!canSeeGroup(ext)) continue;
      items.push({
        type: 'leaf',
        key: ext.key,
        label: ext.label,
        icon: ext.icon,
        order: ext.order,
        externalUrl: ext.externalUrlForEnv ? ext.externalUrlForEnv(currentEnv) : ext.externalUrl,
      });
    }

    // Sort all top-level items by group/external order
    items.sort((a, b) => a.order - b.order);
    return items;
    // `roles.join` keeps the memo stable across identical role sets even if the
    // array identity changes between renders. `hasPermission`'s identity is
    // already stabilized by `useCurrentUser`'s own memo (changes only when the
    // underlying /api/me data changes), so it's a safe direct dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // storyblokOnly MUST stay in this list. useStoryblokMode resolves
  // asynchronously and reports `false` while the query is in flight, so the
  // first pass computes the nav with the legacy CMS group visible. Without the
  // dep the memo never recomputes when the real answer arrives and the sidebar
  // stays frozen on that loading-state guess — the CMS group showing for a
  // Storyblok tenant, while RequireStoryblokMode (which waits for `ready`)
  // correctly redirects those routes to the Content surface. That split is
  // exactly the CPT-1219 bug reported on dev01 and QA.
  //
  // hideV1Actions MUST stay in this list too (CPT-1376). It and taskV2Enabled
  // are independent booleans derived from useActionsDisplayMode's `mode`, and
  // a tenant moving 'both' -> 'task-v2' changes ONLY hideV1Actions
  // (taskV2Enabled is true in both modes). Without this dep, that transition
  // left the memo returning its stale 'both'-mode items — the V1 Actions
  // group stayed visible and Task V2 kept its own label — until something
  // else in the deps changed or the page was reloaded.
  }, [tenantCode, includeAdmin, roles.join(','), isMaster, taskV2Enabled, storyblokOnly, hideV1Actions, rbacV2Enabled, hasPermission]);
}
